import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaUserMd,
  FaUserNurse,
  FaNotesMedical,
  FaPlus,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaRegCalendarAlt,
  FaRegUser,
  FaPaperclip,
  FaEye,
  FaDownload,
} from "react-icons/fa";
import { FiCalendar, FiSearch, FiX } from "react-icons/fi";
import { message } from "antd";
import ClinicalNotesService from "../../../../Services/ClinicalNotesService";
import AddNoteModal from "../ClinicalNotes/AddNoteModal";
import AddNurseActionNoteModal from "./AddNurseActionNoteModal";
import NoteDetailsModal from "../ClinicalNotes/NoteDetailsModal";

const PAGE_SIZE = 50;

const formatDatetime = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return value;
  }
};

const formatDateOnly = (value) => {
  if (!value) return "";
  try {
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

function SkeletonNote() {
  return (
    <div className="border-b border-[#edf1f7] py-4 animate-pulse">
      <div className="px-3 space-y-3">
        <div className="h-3 w-1/3 bg-gray-200 rounded" />
        <div className="h-2 w-1/4 bg-gray-200 rounded" />
        <div className="h-2 w-full bg-gray-200 rounded" />
        <div className="h-2 w-2/3 bg-gray-200 rounded" />
      </div>
    </div>
  );
}

function DoctorNoteCard({ note, onView }) {
  const [expanded, setExpanded] = useState(false);
  const attachments = Array.isArray(note.attachments) ? note.attachments : [];

  return (
    <div className="border border-[#e9edf5] rounded-xl p-5 bg-white shadow-xs hover:shadow-md transition-all duration-200">
      <div
        className="flex items-start justify-between cursor-pointer group"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 pr-4">
          <div className="flex items-center gap-3">
            <h3 className="text-[14px] font-bold text-[#111827]">
              {note.recordedByName || note.createdBy || "Dr. Hari Prakash"}
            </h3>
            <span className="text-[10px] font-semibold text-[#5b5bd6] bg-[#eef2ff] px-2.5 py-0.5 rounded-[4px]">
              {note.role || note.noteTypeName || "Doctor Note"}
            </span>
            {note.status && (
              <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-[4px] border border-emerald-100">
                {note.status}
              </span>
            )}
          </div>
          <p className="text-[13px] text-[#374151] mt-3 leading-relaxed max-w-4xl whitespace-pre-line">
            {note.noteText || note.content || "No note content recorded."}
          </p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <p className="text-[12px] font-medium text-[#4b5563]">
            {formatDatetime(
              note.noteDatetime || note.createdDate || note.datetime,
            )}
          </p>
          {onView && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView(note);
              }}
              title="View note details"
              className="w-7 h-7 flex items-center justify-center rounded-md border border-[#edf1f7] text-[#008f9f] hover:bg-teal-50 bg-white cursor-pointer"
            >
              <FaEye className="text-[12px]" />
            </button>
          )}
          {expanded ? (
            <FaChevronUp className="text-[#6b7280] text-[12px] group-hover:text-[#111827]" />
          ) : (
            <FaChevronDown className="text-[#6b7280] text-[12px] group-hover:text-[#111827]" />
          )}
        </div>
      </div>

      {expanded &&
        (note.nurseAction || attachments.length > 0 || note.count > 0) && (
          <div className="mt-5 mb-1 border border-[#edf1f7] rounded-xl overflow-hidden bg-[#fafbff]">
            <div className="px-4 py-3 flex items-center justify-between border-b border-[#edf1f7]">
              <div className="flex items-center gap-2.5 text-[#4b5563]">
                <FaRegUser className="text-[13px]" />
                <span className="text-[13px] font-semibold text-[#111827]">
                  Follow-up Notes / Attachments{" "}
                  {attachments.length
                    ? `(${attachments.length})`
                    : note.count
                      ? `(${note.count})`
                      : ""}
                </span>
              </div>
              <FaChevronUp className="text-[#6b7280] text-[11px]" />
            </div>

            {/* Nurse Action Section */}
            {note.nurseAction && (
              <div className="p-4 bg-[#fafbff] flex gap-4 border-b border-[#edf1f7] last:border-b-0">
                <div className="flex flex-col items-center pt-1.5">
                  <div className="w-[7px] h-[7px] rounded-full border-[1.5px] border-[#008f9f] bg-white relative z-10"></div>
                  <div className="w-[1.5px] h-full bg-[#e5e7eb] -mt-1"></div>
                </div>
                <div className="pb-2 flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h4 className="text-[13px] font-bold text-[#111827]">
                        {note.nurseAction.assignedNurseName || "Nurse"}
                      </h4>
                      <span className="text-[10px] font-semibold text-[#16a34a] bg-[#dcfce7] px-2.5 py-0.5 rounded-[4px]">
                        Staff Nurse
                      </span>
                    </div>
                    <p className="text-[12px] font-medium text-[#4b5563]">
                      {formatDatetime(note.nurseAction.dueDatetime)}
                    </p>
                  </div>
                  <p className="text-[13px] text-[#374151] mt-2 leading-relaxed">
                    {note.nurseAction.actionInstructions}
                  </p>
                </div>
              </div>
            )}

            {/* Attachments Section */}
            {attachments.length > 0 && (
              <div className="p-4 bg-[#fafbff] space-y-2">
                <p className="text-[12px] font-semibold text-[#374151] mb-2 flex items-center gap-2">
                  <FaPaperclip className="text-[#008f9f] text-[12px]" />
                  Uploaded Attachments ({attachments.length})
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {attachments.map((file, idx) => (
                    <div
                      key={file.attachmentId || idx}
                      onClick={() => onView && onView(note)}
                      className="flex items-center justify-between p-2.5 rounded-lg border border-[#edf1f7] bg-white text-xs cursor-pointer hover:border-[#008f9f] hover:bg-teal-50/30 transition"
                    >
                      <div className="flex items-center gap-2 truncate min-w-0">
                        <FaPaperclip className="text-[#6b7280] shrink-0" />
                        <span className="font-semibold text-gray-800 truncate">
                          {file.fileName || `Attachment_${idx + 1}`}
                        </span>
                        {file.fileSizeKb && (
                          <span className="text-gray-400 text-[11px] shrink-0">
                            ({file.fileSizeKb} KB)
                          </span>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onView && onView(note);
                        }}
                        className="text-[#008f9f] hover:text-[#007a8a] bg-white p-1 font-semibold text-[11px] flex items-center gap-1"
                      >
                        <FaEye className="text-[12px]" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
}

function Notes({ patientId, admissionId, patientDetails }) {
  const [openModal, setOpenModal] = useState(false);
  const [openNurseActionModal, setOpenNurseActionModal] = useState(false);
  const [activeNote, setActiveNote] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const sessionLogin = sessionStorage.getItem("login_result");
  const loginResultObject = JSON.parse(sessionLogin);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ClinicalNotesService.getPatientClinicalNotes({
        // pageNo: 1,
        // pageSize: 10,
        patientId: patientId || undefined,
        recordedBy: loginResultObject?.user?.userId,
      });
      console.log("ClinicalNotes response:", res?.data);
      const listData = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.content)
          ? res.data.content
          : [];
      setNotes(listData);
    } catch (err) {
      console.error("Failed to load clinical notes in Notes tab:", err);
      setError(err.message || "Failed to load notes");
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const filteredNotes = useMemo(() => {
    return notes.filter((n) => {
      // 1. Date Filter
      if (dateFilter) {
        const itemDateStr = n.noteDatetime || n.createdDate || n.datetime || "";
        if (
          !itemDateStr.includes(dateFilter) &&
          formatDateOnly(itemDateStr) !== formatDateOnly(dateFilter)
        ) {
          return false;
        }
      }

      // 2. Search Term
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const text = (n.noteText || n.content || "").toLowerCase();
        const author = (n.createdBy || n.recordedByName || "").toLowerCase();
        const type = (n.type || n.status || n.noteTypeName || "").toLowerCase();
        if (!text.includes(q) && !author.includes(q) && !type.includes(q)) {
          return false;
        }
      }

      return true;
    });
  }, [notes, dateFilter, searchTerm]);

  const sortedNotes = useMemo(() => {
    return [...filteredNotes].sort((a, b) => {
      const timeA =
        new Date(
          (a.noteDatetime || a.createdDate || a.datetime || "").replace(
            " ",
            "T",
          ),
        ).getTime() || 0;
      const timeB =
        new Date(
          (b.noteDatetime || b.createdDate || b.datetime || "").replace(
            " ",
            "T",
          ),
        ).getTime() || 0;
      return timeB - timeA;
    });
  }, [filteredNotes]);

  return (
    <div className="flex flex-col gap-6 py-5">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-bold text-[#111827]">
            Doctor/Nurse Notes
          </h2>
        </div>

        <div className="flex items-center gap-3">
          {/* Date filter */}
          <div className="flex items-center gap-2 border border-[#e5e7eb] rounded-lg px-3 h-10 bg-white min-w-[160px]">
            <FiCalendar className="text-[#6b7280] text-[14px]" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-transparent outline-none text-[12px] text-[#374151] font-semibold cursor-pointer w-full"
            />
            {dateFilter && (
              <button
                onClick={() => setDateFilter("")}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <FiX className="text-[12px]" />
              </button>
            )}
          </div>

          {/* Search box */}

          <button
            onClick={() => setOpenNurseActionModal(true)}
            className="h-10 px-5 rounded-lg bg-[#008f9f] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#007a8a] transition-colors shadow-sm shrink-0 cursor-pointer"
          >
            <FaPlus className="text-[12px]" />
            Add Note
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#e9edf5] rounded-[18px] p-6 shadow-sm">
        {/* CONTENT */}
        <div className="space-y-3.5">
          {loading && (
            <div className="space-y-4 py-4">
              <SkeletonNote />
              <SkeletonNote />
            </div>
          )}

          {!loading && sortedNotes.length === 0 && (
            <div className="py-16 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3 text-gray-400">
                <FaUserMd className="text-lg" />
              </div>
              <p className="text-[14px] font-semibold text-[#374151]">
                No notes found
              </p>
              <p className="text-[12px] text-[#9ca3af] mt-1">
                {searchTerm || dateFilter
                  ? "No notes matched your search/filter criteria."
                  : "No clinical notes recorded yet for this patient."}
              </p>
            </div>
          )}

          {!loading &&
            sortedNotes.map((note, index) => (
              <DoctorNoteCard
                key={note.id || note.noteId || index}
                note={note}
                onView={setActiveNote}
              />
            ))}
        </div>
      </div>

      {/* Note Details Modal */}
      {activeNote && (
        <NoteDetailsModal
          note={activeNote}
          onClose={() => setActiveNote(null)}
        />
      )}

      {/* Add Note Modal */}
      {openModal && (
        <AddNoteModal
          patientDetails={patientDetails}
          patientId={patientId}
          admissionId={admissionId}
          onSuccess={fetchNotes}
          onClose={() => {
            setOpenModal(false);
          }}
        />
      )}

      {/* Add Nurse Action Note Modal */}
      {openNurseActionModal && (
        <AddNurseActionNoteModal
          patientDetails={patientDetails}
          patientId={patientId}
          admissionId={admissionId}
          onSuccess={fetchNotes}
          onClose={() => {
            setOpenNurseActionModal(false);
          }}
        />
      )}
    </div>
  );
}

export default Notes;
